import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { getListingUrl } from '../utils/slugify';
import useSeoOverrides from '../hooks/useSeoOverrides';

const SEO = ({ 
  title, 
  description, 
  keywords,
  type = 'website',
  image,
  url,
  canonicalUrl,
  structuredData,
  noIndex = false,
  breadcrumbs = null,
  seoSlug = null
}) => {
  // Component-instance-scoped tag so unmount cleanup of a previous SEO doesn't
  // race with the current mount's injection on SPA route change.
  const instanceIdRef = useRef();
  if (!instanceIdRef.current) {
    instanceIdRef.current = 'seo-' + Math.random().toString(36).slice(2, 10);
  }
  const instanceId = instanceIdRef.current;

  const overrides = useSeoOverrides(seoSlug);
  const siteName = 'Discover Essex';
  const defaultDescription = 'Your complete guide to local businesses, events, jobs and marketplace listings across Essex. Explore Chelmsford, Colchester, Southend-on-Sea and more.';
  const defaultKeywords = 'Essex directory, local businesses Essex, events Essex, jobs Essex, marketplace listings Essex, Chelmsford, Colchester, Southend-on-Sea';
  const baseUrl = 'https://discoveressex.co.uk';
  
  // Smart title builder: always end with "| Discover Essex", truncate primary to fit
  const buildTitle = (raw) => {
    if (!raw) return `${siteName} | Businesses, Jobs, Events & More`;
    // Already contains brand — use as-is with truncation
    if (raw.includes(siteName)) {
      if (raw.length <= 60) return raw;
      return raw.substring(0, 57).replace(/\s+\S*$/, '') + '...';
    }
    const suffix = ` | ${siteName}`; // 18 chars
    const maxPrimary = 60 - suffix.length; // 42 chars
    if (raw.length <= maxPrimary) return `${raw}${suffix}`;
    // Truncate primary, keep brand
    const truncated = raw.substring(0, maxPrimary - 3).replace(/\s+\S*$/, '') + '...';
    return `${truncated}${suffix}`;
  };

  const capDesc = (text, max = 155) => {
    if (!text || text.length <= max) return text;
    return text.substring(0, max - 3).replace(/\s+\S*$/, '') + '...';
  };

  // Apply admin SEO overrides if available
  const effectiveTitle = (overrides?.title) || title;
  const effectiveDesc = (overrides?.description) || description;
  const effectiveKeywords = (overrides?.keywords) || keywords;
  const effectiveOgTitle = overrides?.og_title || null;
  const effectiveOgDesc = overrides?.og_description || null;
  const effectiveOgImage = overrides?.og_image || image;
  const effectiveNoIndex = overrides?.no_index || noIndex;

  const seoTitle = buildTitle(effectiveTitle);
  const seoDescription = capDesc(effectiveDesc || defaultDescription, 155);
  const seoKeywords = effectiveKeywords || defaultKeywords;
  const seoImage = effectiveOgImage || 'https://discoveressex.co.uk/og-image.jpg';
  const seoUrl = url || baseUrl;
  const ogTitle = effectiveOgTitle || seoTitle;
  const ogDescription = effectiveOgDesc || seoDescription;

  // Direct DOM manipulation fallback — ensures meta tags are set reliably in SPA
  useEffect(() => {
    document.title = seoTitle;
    const setMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    const setLink = (rel, href, extra) => {
      const selector = extra ? `link[rel="${rel}"][${extra}]` : `link[rel="${rel}"]`;
      let el = document.querySelector(selector);
      if (!el) { el = document.createElement('link'); el.rel = rel; if (extra) { const [a,v] = extra.split('='); el.setAttribute(a, v.replace(/"/g,'')); } document.head.appendChild(el); }
      el.href = href;
    };
    setMeta('name', 'description', seoDescription);
    setMeta('name', 'keywords', seoKeywords);
    setMeta('name', 'robots', effectiveNoIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:url', seoUrl);
    setMeta('property', 'og:title', ogTitle);
    setMeta('property', 'og:description', ogDescription);
    setMeta('property', 'og:image', seoImage);
    setMeta('property', 'og:site_name', siteName);
    setMeta('property', 'og:locale', 'en_GB');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', ogTitle);
    setMeta('name', 'twitter:description', ogDescription);
    setMeta('name', 'twitter:image', seoImage);
    setLink('canonical', canonicalUrl || seoUrl);
    setLink('alternate', canonicalUrl || seoUrl, 'hreflang="en-gb"');
  }, [seoTitle, seoDescription, seoKeywords, seoImage, seoUrl, canonicalUrl, type, effectiveNoIndex, ogTitle, ogDescription]);

  // Imperatively inject JSON-LD scripts. react-helmet-async has proven flaky
  // for JSON-LD across some SPA routes (silently drops scripts even though it
  // still updates <title>/<meta>). Rendering them directly into <head> keyed
  // by a stable data-seo-jsonld attribute guarantees crawlers see them.
  useEffect(() => {
    // Build the schema list (mirror of the render-time logic below)
    const list = [];
    if (structuredData) {
      if (Array.isArray(structuredData)) structuredData.forEach(s => s && list.push(s));
      else list.push(structuredData);
    }
    if (breadcrumbs && breadcrumbs.length) {
      list.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': breadcrumbs.map((c, i) => ({ '@type': 'ListItem', 'position': i + 1, 'name': c.name, 'item': c.url }))
      });
    }
    // Remove previously-injected scripts owned by THIS SEO instance before
    // re-adding — scoped by data-seo-instance so a concurrently-unmounting SEO
    // component's cleanup can't strip this instance's freshly-injected scripts
    // during SPA transitions.
    document.querySelectorAll(`script[data-seo-instance="${instanceId}"]`).forEach(el => el.remove());
    list.forEach((sd, i) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-seo-jsonld', '1');
      el.setAttribute('data-seo-instance', instanceId);
      el.setAttribute('data-seo-index', String(i));
      el.textContent = JSON.stringify(sd);
      document.head.appendChild(el);
    });
    return () => {
      document.querySelectorAll(`script[data-seo-instance="${instanceId}"]`).forEach(el => el.remove());
    };
  }, [structuredData, breadcrumbs, instanceId]);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      
      {effectiveNoIndex && <meta name="robots" content="noindex, nofollow" />}
      {!effectiveNoIndex && <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={ogTitle} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_GB" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seoUrl} />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={ogDescription} />
      <meta name="twitter:image" content={seoImage} />
      <meta name="twitter:image:alt" content={ogTitle} />
      
      {/* Canonical URL - use canonicalUrl if provided to consolidate filtered pages */}
      <link rel="canonical" href={canonicalUrl || seoUrl} />
      
      {/* Hreflang - English GB primary, with x-default */}
      <link rel="alternate" hrefLang="en-gb" href={canonicalUrl || seoUrl} />
      <link rel="alternate" hrefLang="en" href={canonicalUrl || seoUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl || seoUrl} />
      
      {/* Geographic Meta Tags for Local SEO */}
      <meta name="geo.region" content="GB-ESS" />
      <meta name="geo.placename" content="Essex" />

      {/* JSON-LD structured data is injected imperatively in the useEffect
          above (data-seo-jsonld="1"). react-helmet-async has proven unreliable
          for <script type="application/ld+json"> on some SPA routes, so we
          bypass it for schemas to guarantee crawlers pick them up. */}
    </Helmet>
  );
};

// Organization Schema for the website
export const OrganizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  'name': 'Discover Essex',
  'url': 'https://discoveressex.co.uk',
  'logo': 'https://discoveressex.co.uk/logo.png',
  'description': 'Essex\'s premier local directory for businesses, events, jobs and marketplace listings.',
  'areaServed': {
    '@type': 'State',
    'name': 'Essex',
    'containedInPlace': {
      '@type': 'Country',
      'name': 'United Kingdom'
    }
  },
  'sameAs': [
    'https://www.facebook.com/discoveressex',
    'https://twitter.com/discoveressex',
    'https://www.instagram.com/discoveressex'
  ]
};

// WebSite Schema with SearchAction
export const WebSiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  'name': 'Discover Essex',
  'url': 'https://discoveressex.co.uk',
  'potentialAction': {
    '@type': 'SearchAction',
    'target': {
      '@type': 'EntryPoint',
      'urlTemplate': 'https://discoveressex.co.uk/search?q={search_term_string}'
    },
    'query-input': 'required name=search_term_string'
  }
};

// Build JSON-LD structured data for a listing
function buildStructuredData(listing, baseUrl) {
  if (!listing) return null;
  const l = listing;
  const title = l.title || '';
  const town = l.town || 'Essex';
  const category = l.category || '';
  const url = `${baseUrl}/listing/${l.id}`;

  if (l.listing_type === 'job') {
    const sd = {
      "@context": "https://schema.org", "@type": "JobPosting",
      "title": title,
      "description": l.description || `${title} — ${category} job in ${town}, Essex.`,
      "datePosted": l.created_at || new Date().toISOString(),
      "hiringOrganization": { "@type": "Organization", "name": l.company_name || l.contact_name || "Employer in Essex" },
      "jobLocation": { "@type": "Place", "address": { "@type": "PostalAddress", "addressLocality": town, "addressRegion": "Essex", "addressCountry": "GB" } },
      "employmentType": l.job_type ? l.job_type.toUpperCase().replace(/\s+/g, '_') : "FULL_TIME",
      "url": url,
    };
    if (l.salary_min && l.salary_max) sd.baseSalary = { "@type": "MonetaryAmount", "currency": "GBP", "value": { "@type": "QuantitativeValue", "minValue": l.salary_min, "maxValue": l.salary_max, "unitText": "YEAR" } };
    if (l.expires_at) sd.validThrough = l.expires_at;
    if (l.image_url) sd.image = l.image_url;
    return sd;
  }

  if (l.listing_type === 'event') {
    const sd = {
      "@context": "https://schema.org", "@type": "Event",
      "name": title,
      "description": l.meta_description || l.description || `${title} — event in ${town}, Essex.`,
      "startDate": l.event_date || l.start_date || l.created_at || new Date().toISOString(),
      "location": {
        "@type": "Place",
        "name": l.venue_name || l.venue || l.address || town,
        "address": { "@type": "PostalAddress", "streetAddress": l.address || '', "addressLocality": town, "addressRegion": "Essex", "postalCode": l.postcode || '', "addressCountry": "GB" }
      },
      "organizer": { "@type": "Organization", "name": l.organizer_name || l.contact_name || "Discover Essex" },
      "eventStatus": "https://schema.org/EventScheduled",
      "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
      "url": url,
    };
    if (l.event_end_date) sd.endDate = l.event_end_date;
    if (l.end_date) sd.endDate = l.end_date;
    if (l.image_url) sd.image = l.image_url;
    if (l.ticket_url || l.website) {
      sd.offers = {
        "@type": "Offer",
        "url": l.ticket_url || l.website || url,
        "availability": "https://schema.org/InStock",
      };
      if (l.price_range) {
        const priceMatch = (l.price_range || '').match(/[\d.]+/);
        if (priceMatch) {
          sd.offers.price = priceMatch[0];
          sd.offers.priceCurrency = "GBP";
        }
      } else if (l.price || l.ticket_price) {
        sd.offers.price = String(l.price || l.ticket_price);
        sd.offers.priceCurrency = "GBP";
      }
    }
    return sd;
  }

  // LocalBusiness (default)
  const sd = {
    "@context": "https://schema.org", "@type": "LocalBusiness",
    "name": title,
    "description": l.description || `${title} — ${category} in ${town}, Essex.`,
    "url": url,
    "address": { "@type": "PostalAddress", "addressLocality": town, "addressRegion": "Essex", "addressCountry": "GB" },
  };
  if (l.phone) sd.telephone = l.phone;
  if (l.website) sd.sameAs = l.website;
  if (l.image_url) sd.image = l.image_url;
  if (l.rating && l.review_count) sd.aggregateRating = { "@type": "AggregateRating", "ratingValue": l.rating, "reviewCount": l.review_count, "bestRating": 5 };
  return sd;
}


// Pre-built SEO configs for common pages
export const ListingSEO = ({ listing }) => {
  const typeLabels = {
    business: 'Business',
    event: 'Event',
    job: 'Job',
    classified: 'Marketplace'
  };
  const typePluralLabels = {
    business: 'Businesses',
    event: 'Events',
    job: 'Jobs',
    classified: 'Marketplace'
  };
  
  const baseUrl = 'https://discoveressex.co.uk';
  
  // Get SEO-friendly URL for this listing
  const listingUrl = listing ? `${baseUrl}${getListingUrl(listing)}` : baseUrl;
  
  // Generate unique title and description based on listing type
  const generateSeoContent = () => {
    if (!listing) return { title: '', description: '', keywords: '' };
    
    const town = (listing.town || 'Essex').trim();
    const category = (listing.category || '').trim();
    const title = (listing.title || '').trim();
    const descSnippet = (listing.description || '').substring(0, 120).trim();
    
    switch (listing.listing_type) {
      case 'business': {
        // Preferred: Name | Category | Discover Essex
        // Fallback:  Name, Town | Discover Essex
        const fullTitle = `${title} | ${category} | Discover Essex`;
        const titleStr = fullTitle.length <= 60
          ? `${title} | ${category}`
          : `${title}, ${town}`;
        return {
          title: titleStr,
          description: `${title} — ${category} in ${town}, Essex. ${descSnippet}`,
          keywords: `${title}, ${category} ${town}, ${category} Essex, local business ${town}`
        };
      }
      
      case 'job':
        return {
          title: `${title}, ${town} | Jobs`,
          description: `Apply for ${title} in ${town}, Essex.${listing.company_name ? ` At ${listing.company_name}.` : ''} ${descSnippet}`,
          keywords: `${title} job, ${category} jobs ${town}, jobs in ${town}, ${town} vacancies, Essex employment`
        };
      
      case 'event':
        const eventDate = listing.event_date ? new Date(listing.event_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
        return {
          title: `${title}, ${town} | Event`,
          description: listing.meta_description || `${title} — ${category} event in ${town}${eventDate ? ` on ${eventDate}` : ''}. ${descSnippet}`,
          keywords: `${title}, ${category} event ${town}, events in ${town}, what's on ${town}, Essex events`
        };
      
      case 'classified':
        return {
          title: `${title} | For Sale in ${town}`,
          description: `${title} — ${category} for sale in ${town}, Essex. ${descSnippet}`,
          keywords: `${title} for sale, ${category} ${town}, buy ${category} Essex, marketplace ${town}`
        };
      
      default:
        return {
          title: `${title} - ${typeLabels[listing.listing_type]} in ${town}`,
          description: `${descSnippet}... Find ${typeLabels[listing.listing_type]?.toLowerCase()}s in ${town}, Essex.`,
          keywords: `${title}, ${category}, ${town}, Essex`
        };
    }
  };
  
  const seoContent = generateSeoContent();

  // Inject structured data directly into DOM (must be before any early return for hooks consistency)
  React.useEffect(() => {
    const id = 'listing-ld-json';
    if (!listing) {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      return;
    }
    // Build structuredData inside effect to keep it after all hooks
    const sd = buildStructuredData(listing, baseUrl);
    if (!sd) return;
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(sd);
    return () => { const e = document.getElementById(id); if (e) e.remove(); };
  }, [listing, baseUrl]);
  
  if (!listing) return null;
  
  // Process image URL for OG tags
  // Social media crawlers need absolute URLs and specific dimensions
  const getOGImage = () => {
    // Events: use the event's own image (Ticketmaster poster etc.)
    if (listing.listing_type === 'event' && listing.image_url) {
      return listing.image_url;
    }
    // Events without image: dynamic branded OG with event text
    if (listing.listing_type === 'event' && listing.id) {
      return `https://discoveressex.co.uk/api/og-image/event/${listing.id}`;
    }

    if (!listing.image_url) {
      return 'https://discoveressex.co.uk/og-image.jpg';
    }
    
    let imageUrl = listing.image_url;
    
    // Handle relative URLs (local uploads)
    if (imageUrl.startsWith('/')) {
      imageUrl = `${baseUrl}${imageUrl}`;
    }
    
    // Handle Cloudinary URLs - add transformations for OG compliance (1200x630)
    if (imageUrl.includes('cloudinary.com')) {
      // Insert transformation parameters for optimal OG image
      // Format: /upload/c_fill,w_1200,h_630,f_jpg,q_auto/
      imageUrl = imageUrl.replace(
        '/upload/',
        '/upload/c_fill,w_1200,h_630,f_jpg,q_auto/'
      );
    }
    
    return imageUrl;
  };
  
  const ogImage = getOGImage();
  
  // Build type-specific structured data
  let structuredData;
  
  if (listing.listing_type === 'business') {
    structuredData = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': listingUrl,
      'name': listing.title,
      'description': listing.description,
      'image': ogImage,
      'url': listingUrl,
      'telephone': listing.phone,
      'email': listing.email,
      'address': {
        '@type': 'PostalAddress',
        'streetAddress': listing.address,
        'addressLocality': listing.town,
        'postalCode': listing.postcode,
        'addressRegion': 'Essex',
        'addressCountry': 'GB'
      },
      'geo': listing.latitude && listing.longitude ? {
        '@type': 'GeoCoordinates',
        'latitude': listing.latitude,
        'longitude': listing.longitude
      } : undefined,
      'priceRange': listing.price_range || '££',
      ...(listing.opening_hours && {
        'openingHoursSpecification': parseOpeningHours(listing.opening_hours)
      }),
      ...(listing.rating > 0 && {
        'aggregateRating': {
          '@type': 'AggregateRating',
          'ratingValue': listing.rating,
          'reviewCount': listing.review_count,
          'bestRating': 5,
          'worstRating': 1
        }
      }),
      'areaServed': {
        '@type': 'City',
        'name': listing.town
      }
    };
  } else if (listing.listing_type === 'event') {
    // Fallbacks ensure every required Schema.org Event field is present.
    // Google flags `startDate`/`location` as MISSING when null/empty.
    const eventStart =
      listing.event_date ||
      listing.start_date ||
      listing.event_start ||
      listing.created_at ||
      new Date().toISOString();
    const venueName =
      listing.venue_name ||
      listing.venue ||
      listing.location_name ||
      listing.title ||
      listing.town ||
      'Essex';
    const streetAddress = listing.address || venueName || listing.town || 'Essex';
    const organizerName =
      listing.organizer ||
      listing.organizer_name ||
      listing.contact_name ||
      listing.business_name ||
      'Discover Essex';

    structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Event',
      '@id': listingUrl,
      'name': listing.title,
      'description': listing.description || `${listing.title} — event in ${listing.town || 'Essex'}.`,
      'image': ogImage,
      'url': listingUrl,
      'startDate': eventStart,
      'endDate': listing.event_end_date || listing.end_date || eventStart,
      'eventStatus': 'https://schema.org/EventScheduled',
      'eventAttendanceMode': 'https://schema.org/OfflineEventAttendanceMode',
      'location': {
        '@type': 'Place',
        'name': venueName,
        'address': {
          '@type': 'PostalAddress',
          'streetAddress': streetAddress,
          'addressLocality': listing.town || 'Essex',
          'postalCode': listing.postcode || '',
          'addressRegion': 'Essex',
          'addressCountry': 'GB'
        }
      },
      'organizer': {
        '@type': 'Organization',
        'name': organizerName,
        'url': listing.website || baseUrl
      },
      ...(listing.ticket_price && {
        'offers': {
          '@type': 'Offer',
          'price': listing.ticket_price,
          'priceCurrency': 'GBP',
          'availability': 'https://schema.org/InStock',
          'validFrom': new Date().toISOString(),
          'url': listingUrl
        }
      })
    };
  } else if (listing.listing_type === 'job') {
    const salaryParts = listing.salary ? parseSalary(listing.salary) : null;
    
    structuredData = {
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      '@id': listingUrl,
      'title': listing.title,
      'description': listing.description,
      'datePosted': listing.created_at,
      'validThrough': listing.expires_at || addDays(listing.created_at, 30),
      'employmentType': mapEmploymentType(listing.position_type),
      'hiringOrganization': {
        '@type': 'Organization',
        'name': listing.company_name || 'Company on Discover Essex',
        'sameAs': baseUrl,
        'logo': listing.company_logo || `${baseUrl}/logo.png`
      },
      'jobLocation': {
        '@type': 'Place',
        'address': {
          '@type': 'PostalAddress',
          'streetAddress': listing.address,
          'addressLocality': listing.town,
          'postalCode': listing.postcode,
          'addressRegion': 'Essex',
          'addressCountry': 'GB'
        }
      },
      ...(salaryParts && {
        'baseSalary': {
          '@type': 'MonetaryAmount',
          'currency': 'GBP',
          'value': {
            '@type': 'QuantitativeValue',
            'minValue': salaryParts.min,
            'maxValue': salaryParts.max,
            'unitText': salaryParts.period
          }
        }
      }),
      'jobBenefits': listing.benefits || undefined,
      'qualifications': listing.requirements || undefined
    };
  } else if (listing.listing_type === 'classified') {
    // Use classified_price field for marketplace listings
    const price = listing.classified_price || listing.price || 0;
    
    structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      '@id': listingUrl,
      'name': listing.title,
      'description': listing.description,
      'image': ogImage,
      'url': listingUrl,
      'category': listing.category,
      'brand': listing.brand || undefined,
      'itemCondition': mapItemCondition(listing.classified_condition || listing.condition),
      'offers': {
        '@type': 'Offer',
        'price': price,
        'priceCurrency': 'GBP',
        'availability': listing.status === 'sold' ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
        'itemCondition': mapItemCondition(listing.classified_condition || listing.condition),
        'priceValidUntil': addDays(new Date().toISOString(), 30),
        'seller': {
          '@type': 'Person',
          'name': listing.owner_name || 'Seller'
        },
        'areaServed': {
          '@type': 'City',
          'name': listing.town
        }
      },
      ...(listing.accepts_offers && {
        'acceptsReservations': true
      })
    };
  }
  
  // Breadcrumbs - map listing types to correct URL paths
  const getListingTypePath = (type) => {
    switch (type) {
      case 'business': return '/business';
      case 'job': return '/jobs';
      case 'event': return '/events';
      case 'classified': return '/marketplace';
      default: return '/';
    }
  };
  
  const breadcrumbs = [
    { name: 'Home', url: baseUrl },
    { name: typePluralLabels[listing.listing_type] || 'Listings', url: `${baseUrl}${getListingTypePath(listing.listing_type)}` },
    { name: listing.category, url: `${baseUrl}${getListingTypePath(listing.listing_type)}?category=${encodeURIComponent(listing.category)}` },
    { name: listing.title, url: listingUrl }
  ];

  // Determine noindex: archived listings and past events should not be indexed
  const shouldNoIndex = listing.status === 'archived' 
    || listing.is_expired === true
    || (listing.listing_type === 'event' && listing.event_date && new Date(listing.event_date) < new Date());

  return (
    <SEO
      title={seoContent.title}
      description={seoContent.description}
      keywords={seoContent.keywords}
      type={listing.listing_type === 'business' ? 'business.business' : listing.listing_type === 'event' ? 'event' : 'article'}
      image={ogImage}
      url={listingUrl}
      noIndex={shouldNoIndex}
      breadcrumbs={breadcrumbs}
    />
  );
};

export const SearchResultsSEO = ({ listingType, category, town, count, listings = [], searchQuery }) => {
  const typeLabels = {
    business: 'Businesses',
    event: 'Events',
    job: 'Jobs',
    classified: 'Marketplace',
    request: 'Service Requests'
  };
  
  const baseUrl = 'https://discoveressex.co.uk';
  
  let title, description;
  
  if (searchQuery) {
    const q = searchQuery.length > 25 ? searchQuery.substring(0, 25) + '...' : searchQuery;
    title = `"${q}" | Search`;
    description = `Search results for "${searchQuery}" across Essex businesses, jobs, events and marketplace on Discover Essex, your free local directory.`;
  } else if (listingType && listingType !== 'all') {
    title = typeLabels[listingType] || 'Listings';
    if (category) title = `${category} ${title}`;
    if (town) title += ` in ${town}`;
    description = `Find trusted ${typeLabels[listingType]?.toLowerCase() || 'listings'}${category ? ` in ${category}` : ''}${town ? ` in ${town}` : ''} on Discover Essex. Browse ${count || ''} verified local listings.`;
  } else {
    title = 'Search Essex';
    description = `Search businesses, jobs, events and marketplace listings across Essex on Discover Essex, your free local directory.`;
  }
  
  const keywords = [
    listingType ? `${typeLabels[listingType]?.toLowerCase()} Essex` : 'Essex directory',
    category || '', town || '',
    'local businesses Essex', 'jobs in Essex', 'Essex events', 'Essex marketplace',
    searchQuery || ''
  ].filter(Boolean).join(', ');
  
  // ItemList structured data for search results
  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    'name': title,
    'description': description,
    'numberOfItems': count || listings.length,
    'itemListElement': listings.slice(0, 10).map((item, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'url': `${baseUrl}/listing/${item.id}`,
      'name': item.title
    }))
  };
  
  // Breadcrumbs
  const breadcrumbs = [
    { name: 'Home', url: baseUrl },
    { name: searchQuery ? `Search: ${searchQuery}` : (typeLabels[listingType] || 'Browse All'), url: `${baseUrl}/search` }
  ];
  if (category) {
    breadcrumbs.push({ name: category, url: `${baseUrl}/search?category=${encodeURIComponent(category)}` });
  }
  if (town) {
    breadcrumbs.push({ name: town, url: `${baseUrl}/search?town=${encodeURIComponent(town)}` });
  }
  
  return (
    <SEO
      title={title}
      description={description}
      keywords={keywords}
      type="website"
      structuredData={itemListSchema}
      breadcrumbs={breadcrumbs}
      noIndex={true}
    />
  );
};

export const CategorySEO = ({ category, listingType }) => {
  const typeLabels = {
    business: 'Businesses',
    event: 'Events', 
    job: 'Jobs',
    classified: 'Marketplace'
  };
  
  const baseUrl = 'https://discoveressex.co.uk';
  
  const breadcrumbs = [
    { name: 'Home', url: baseUrl },
    { name: typeLabels[listingType] || 'Listings', url: `${baseUrl}/${listingType}s` },
    { name: category, url: `${baseUrl}/${listingType}s?category=${encodeURIComponent(category)}` }
  ];
  
  return (
    <SEO
      title={`${category} ${typeLabels[listingType] || 'Listings'} in Essex`}
      description={`Find trusted ${category.toLowerCase()} ${typeLabels[listingType]?.toLowerCase() || 'listings'} across Essex on Discover Essex. Browse verified local options in Chelmsford, Colchester, Southend and more.`}
      keywords={`${category} Essex, ${category} ${typeLabels[listingType]}, Essex ${category.toLowerCase()}`}
      breadcrumbs={breadcrumbs}
    />
  );
};

// Home page SEO with Organization, WebSite and LocalBusiness schema
export const HomeSEO = () => {
  const baseUrl = 'https://discoveressex.co.uk';
  
  const localDirectorySchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    'name': 'Discover Essex',
    'url': baseUrl,
    'applicationCategory': 'BusinessApplication',
    'operatingSystem': 'Web',
    'description': 'Free Essex directory for local businesses, jobs, events and marketplace listings. Find trusted services in Chelmsford, Colchester, Southend & more.',
    'offers': {
      '@type': 'Offer',
      'price': '0',
      'priceCurrency': 'GBP',
      'description': 'Free to list your business, post jobs, events and marketplace items'
    },
    'areaServed': {
      '@type': 'State',
      'name': 'Essex',
      'containedInPlace': { '@type': 'Country', 'name': 'United Kingdom' }
    },
    'author': { '@type': 'Organization', 'name': 'Discover Essex', 'url': baseUrl }
  };

  const combinedSchema = [OrganizationSchema, WebSiteSchema, localDirectorySchema];
  
  return (
    <SEO
      seoSlug="home"
      title="Discover Essex | Businesses, Jobs, Events & Community Hub"
      description="Find local businesses, jobs, events and marketplace listings across Essex on Discover Essex, your free directory for Chelmsford, Colchester and more."
      keywords="Essex directory, local businesses Essex, events Essex, jobs Essex, marketplace listings Essex, community hub Essex, Chelmsford businesses, Colchester events, Southend jobs, what's on Essex, Essex tradespeople, things to do in Essex"
      structuredData={combinedSchema}
      url={baseUrl}
    />
  );
};

// FAQ Page SEO
export const FAQPageSEO = ({ faqs, title = 'Frequently Asked Questions' }) => {
  const baseUrl = 'https://discoveressex.co.uk';
  
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map(faq => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer
      }
    }))
  };
  
  return (
    <SEO
      title="FAQ | Discover Essex"
      description="Find answers to commonly asked questions about Discover Essex. Get help with listings, accounts, payments and using our free Essex directory platform."
      keywords="FAQ, frequently asked questions, Discover Essex help, Essex directory help"
      url={`${baseUrl}/faq`}
      structuredData={faqSchema}
    />
  );
};

// Town/Location specific SEO
export const TownSEO = ({ town, listingType, count }) => {
  const typeLabels = {
    business: 'Businesses',
    event: 'Events',
    job: 'Jobs',
    classified: 'Marketplace'
  };
  
  const baseUrl = 'https://discoveressex.co.uk';
  
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    'name': `${typeLabels[listingType] || 'Listings'} in ${town}, Essex`,
    'description': `Browse ${count || ''} ${typeLabels[listingType]?.toLowerCase() || 'listings'} in ${town}, Essex.`,
    'about': {
      '@type': 'City',
      'name': town,
      'containedInPlace': {
        '@type': 'State',
        'name': 'Essex',
        'containedInPlace': {
          '@type': 'Country',
          'name': 'United Kingdom'
        }
      }
    }
  };
  
  const breadcrumbs = [
    { name: 'Home', url: baseUrl },
    { name: typeLabels[listingType] || 'Listings', url: `${baseUrl}/${listingType}s` },
    { name: town, url: `${baseUrl}/${listingType}s?town=${encodeURIComponent(town)}` }
  ];
  
  return (
    <SEO
      title={`${typeLabels[listingType] || 'Listings'} in ${town} | Essex Directory`}
      description={`Discover ${typeLabels[listingType]?.toLowerCase() || 'listings'} in ${town}, Essex. Browse local options, read reviews and find the best services near you.`}
      keywords={`${town} ${listingType}, ${town} Essex, local ${listingType} ${town}, ${town} directory`}
      structuredData={localBusinessSchema}
      breadcrumbs={breadcrumbs}
    />
  );
};

// Review Schema for businesses with reviews
export const ReviewsSEO = ({ listing, reviews }) => {
  React.useEffect(() => {
    const id = 'reviews-ld-json';
    if (!listing || !reviews?.length) {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      return;
    }
    const baseUrl = 'https://discoveressex.co.uk';
    const listingUrl = `${baseUrl}${getListingUrl(listing)}`;
    const reviewSchema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': listingUrl,
      'name': listing.title,
      'review': reviews.slice(0, 10).map(review => ({
        '@type': 'Review',
        'reviewRating': { '@type': 'Rating', 'ratingValue': review.rating, 'bestRating': 5, 'worstRating': 1 },
        'author': { '@type': 'Person', 'name': review.user_name || 'Anonymous' },
        'reviewBody': review.comment,
        'datePublished': review.created_at
      })),
      ...(listing.rating > 0 && { 'aggregateRating': { '@type': 'AggregateRating', 'ratingValue': listing.rating, 'reviewCount': listing.review_count || reviews.length, 'bestRating': 5, 'worstRating': 1 } })
    };
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(reviewSchema);
    return () => { const e = document.getElementById(id); if (e) e.remove(); };
  }, [listing, reviews]);
  return null;
};

// About/Contact page SEO
export const AboutSEO = () => {
  const baseUrl = 'https://discoveressex.co.uk';
  
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    'mainEntity': {
      '@type': 'Organization',
      'name': 'Discover Essex',
      'description': 'Essex\'s premier local directory platform connecting businesses, events, jobs and marketplace listings with the local community.',
      'foundingDate': '2024',
      'areaServed': {
        '@type': 'State',
        'name': 'Essex',
        'containedInPlace': {
          '@type': 'Country',
          'name': 'United Kingdom'
        }
      }
    }
  };
  
  return (
    <SEO
      title="About | Discover Essex"
      description="Learn about Discover Essex, the free local directory connecting businesses, events, jobs and the community across all Essex towns. Built for Essex."
      keywords="about Discover Essex, Essex directory, local platform Essex"
      url={`${baseUrl}/about`}
      structuredData={aboutSchema}
    />
  );
};

// Pricing page SEO
export const PricingSEO = () => {
  const baseUrl = 'https://discoveressex.co.uk';
  
  return (
    <SEO
      seoSlug="pricing"
      title="Pricing Plans | Discover Essex"
      description="List your business, job, event or marketplace item on Discover Essex. Choose from free and premium plans designed for Essex businesses and services."
      keywords="Essex listing pricing, advertise Essex, business listing Essex, job posting Essex"
      url={`${baseUrl}/pricing`}
      breadcrumbs={[
        { name: 'Home', url: baseUrl },
        { name: 'Pricing', url: `${baseUrl}/pricing` }
      ]}
    />
  );
};

// Helper functions
function parseOpeningHours(hoursString) {
  // Parse opening hours string into schema format
  // Example: "Mon-Fri 9am-5pm, Sat 10am-4pm"
  // This is a simplified implementation
  return undefined; // Return undefined for now, can be enhanced later
}

function parseSalary(salaryString) {
  // Parse salary string like "£45,000 - £60,000" or "£25,000"
  if (!salaryString) return null;
  
  const cleaned = salaryString.replace(/[£,]/g, '');
  const parts = cleaned.split('-').map(p => parseInt(p.trim(), 10));
  
  if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
    return { min: parts[0], max: parts[1], period: 'YEAR' };
  } else if (parts.length === 1 && !isNaN(parts[0])) {
    return { min: parts[0], max: parts[0], period: 'YEAR' };
  }
  return null;
}

function mapEmploymentType(positionType) {
  const mapping = {
    'full-time': 'FULL_TIME',
    'full_time': 'FULL_TIME',
    'part-time': 'PART_TIME',
    'part_time': 'PART_TIME',
    'contract': 'CONTRACTOR',
    'temporary': 'TEMPORARY',
    'internship': 'INTERN',
    'freelance': 'CONTRACTOR'
  };
  return mapping[positionType?.toLowerCase()] || 'FULL_TIME';
}

function mapItemCondition(condition) {
  const mapping = {
    'new': 'https://schema.org/NewCondition',
    'like_new': 'https://schema.org/UsedCondition',
    'good': 'https://schema.org/UsedCondition',
    'fair': 'https://schema.org/UsedCondition',
    'poor': 'https://schema.org/DamagedCondition'
  };
  return mapping[condition?.toLowerCase()] || 'https://schema.org/UsedCondition';
}

function addDays(dateString, days) {
  const date = new Date(dateString);
  date.setDate(date.getDate() + days);
  return date.toISOString();
}

export default SEO;
